import { openDB } from 'idb';

const DB_NAME = 'EZBookmarksDB';
const DB_VERSION = 1;
const BOOKMARKS_STORE = 'bookmarks';
const PENDING_ACTIONS_STORE = 'pendingActions';

class OfflineStorageService {
  constructor() {
    this.db = null;
    this.initDB();
  }

  async initDB() {
    if (!this.db) {
      try {
        this.db = await openDB(DB_NAME, DB_VERSION, {
          upgrade(db) {
            // Create bookmarks store
            if (!db.objectStoreNames.contains(BOOKMARKS_STORE)) {
              const bookmarksStore = db.createObjectStore(BOOKMARKS_STORE, { 
                keyPath: 'id' 
              });
              bookmarksStore.createIndex('userId', 'userId', { unique: false });
              bookmarksStore.createIndex('createdAt', 'createdAt', { unique: false });
            }

            // Create pending actions store for sync when online
            if (!db.objectStoreNames.contains(PENDING_ACTIONS_STORE)) {
              const actionsStore = db.createObjectStore(PENDING_ACTIONS_STORE, { 
                keyPath: 'id',
                autoIncrement: true 
              });
              actionsStore.createIndex('userId', 'userId', { unique: false });
              actionsStore.createIndex('timestamp', 'timestamp', { unique: false });
            }
          },
        });
      } catch (error) {
        console.error('❌ Failed to initialize IndexedDB:', error);
        throw error;
      }
    }
    return this.db;
  }

  // Clear all existing bookmarks for user (used before fresh save from server)
  async clearAllBookmarks(userId) {
    const db = await this.initDB();
    const tx = db.transaction(BOOKMARKS_STORE, 'readwrite');
    const index = tx.store.index('userId');
    const existingBookmarks = await index.getAll(userId);
    
    for (const bookmark of existingBookmarks) {
      await tx.store.delete(bookmark.id);
    }
    await tx.done;
  }

  // Bookmark CRUD operations
  async saveBookmarks(bookmarks, userId) {
    const db = await this.initDB();
    await this.clearAllBookmarks(userId);
    const tx = db.transaction(BOOKMARKS_STORE, 'readwrite');
    for (const bookmark of bookmarks) {
      // When saving from server, isOffline should be false.
      const bookmarkToSave = { ...bookmark, userId, isOffline: false };
      await tx.store.put(bookmarkToSave);
    }
    await tx.done;
  }

  async getBookmarks(userId) {
    const db = await this.initDB();
    const tx = db.transaction(BOOKMARKS_STORE, 'readonly');
    const index = tx.store.index('userId');
    const bookmarks = await index.getAll(userId);
    
    const sortedBookmarks = bookmarks.sort((a, b) => {
      const dateA = a.createdAt?.seconds ? new Date(a.createdAt.seconds * 1000) : new Date(a.createdAt);
      const dateB = b.createdAt?.seconds ? new Date(b.createdAt.seconds * 1000) : new Date(b.createdAt);
      return dateB - dateA;
    });
    
    return sortedBookmarks;
  }

  async addBookmark(bookmark, userId) {
    const db = await this.initDB();
    const bookmarkWithDetails = {
      ...bookmark,
      id: bookmark.id || `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      userId,
      createdAt: bookmark.createdAt || new Date(),
      updatedAt: bookmark.updatedAt || new Date(),
      // Ensure isOffline is explicitly set if provided, otherwise default to false (or true if it's an offline-first add)
      isOffline: typeof bookmark.isOffline === 'boolean' ? bookmark.isOffline : true 
    };
    
    const tx = db.transaction(BOOKMARKS_STORE, 'readwrite');
    await tx.store.put(bookmarkWithDetails);
    await tx.done;
    
    return bookmarkWithDetails;
  }

  async updateBookmark(id, updates, userId) {
    const db = await this.initDB();
    const tx = db.transaction(BOOKMARKS_STORE, 'readwrite');
    const bookmark = await tx.store.get(id);
    
    if (!bookmark || bookmark.userId !== userId) {
      throw new Error('Bookmark not found or access denied');
    }
    
    const updatedBookmark = {
      ...bookmark,
      ...updates,
      updatedAt: new Date(),
      isOfflineUpdated: true // Mark as offline updated
    };
    
    await tx.store.put(updatedBookmark);
    await tx.done;
    
    return updatedBookmark;
  }

  async deleteBookmark(id, userId) {
    const db = await this.initDB();
    const tx = db.transaction(BOOKMARKS_STORE, 'readwrite');
    const bookmark = await tx.store.get(id);
    
    if (!bookmark || bookmark.userId !== userId) {
      throw new Error('Bookmark not found or access denied');
    }
    
    await tx.store.delete(id);
    await tx.done;
    
    return id;
  }

  // Pending actions for sync when online
  async addPendingAction(action, userId) {
    const db = await this.initDB();
    const existingActions = await this.getPendingActions(userId);
    const isDuplicate = existingActions.some(existing => {
      if (action.type === 'add' && existing.type === 'add' && 
          action.tempId && existing.tempId === action.tempId) {
        return true;
      }
      if ((action.type === 'update' || action.type === 'delete') && 
          existing.type === action.type && 
          action.bookmarkId && existing.bookmarkId === action.bookmarkId) {
        return true;
      }
      return false;
    });

    if (isDuplicate) {
      return;
    }
    
    const tx = db.transaction(PENDING_ACTIONS_STORE, 'readwrite');
    const pendingAction = {
      ...action,
      userId,
      timestamp: new Date(),
      retryCount: 0
    };
    await tx.store.add(pendingAction);
    await tx.done;
  }

  async getPendingActions(userId) {
    const db = await this.initDB();
    const tx = db.transaction(PENDING_ACTIONS_STORE, 'readonly');
    const index = tx.store.index('userId');
    const actions = await index.getAll(userId);
    return actions;
  }

  async removePendingAction(actionId) {
    const db = await this.initDB();
    const tx = db.transaction(PENDING_ACTIONS_STORE, 'readwrite');
    await tx.store.delete(actionId);
    await tx.done;
  }

  async clearPendingActions(userId) {
    const db = await this.initDB();
    const tx = db.transaction(PENDING_ACTIONS_STORE, 'readwrite');
    const index = tx.store.index('userId');
    const actions = await index.getAll(userId);
    
    for (const action of actions) {
      await tx.store.delete(action.id);
    }
    
    await tx.done;
  }

  // Search bookmarks offline
  async searchBookmarks(searchTerm, userId) {
    const bookmarks = await this.getBookmarks(userId);
    
    if (!searchTerm.trim()) return bookmarks;
    
    const term = searchTerm.toLowerCase();
    return bookmarks.filter(bookmark => 
      bookmark.title?.toLowerCase().includes(term) ||
      bookmark.url?.toLowerCase().includes(term) ||
      bookmark.description?.toLowerCase().includes(term) ||
      bookmark.tags?.some(tag => tag.toLowerCase().includes(term))
    );
  }

  // Clear all data for a user
  async clearUserData(userId) {
    const db = await this.initDB();
    
    // Clear bookmarks
    const bookmarksTx = db.transaction(BOOKMARKS_STORE, 'readwrite');
    const bookmarksIndex = bookmarksTx.store.index('userId');
    const userBookmarks = await bookmarksIndex.getAll(userId);
    
    for (const bookmark of userBookmarks) {
      await bookmarksTx.store.delete(bookmark.id);
    }
    await bookmarksTx.done;
    
    // Clear pending actions
    await this.clearPendingActions(userId);
  }

  // Check if we have offline data for user
  async hasOfflineData(userId) {
    const bookmarks = await this.getBookmarks(userId);
    return bookmarks.length > 0;
  }

  async cleanupOrphanedActions(userId) {
    const db = await this.initDB();
    const tx = db.transaction([PENDING_ACTIONS_STORE, BOOKMARKS_STORE], 'readwrite');
    const actionsStore = tx.objectStore(PENDING_ACTIONS_STORE);
    const bookmarksStore = tx.objectStore(BOOKMARKS_STORE);
    
    const actionsIndex = actionsStore.index('userId');
    const actions = await actionsIndex.getAll(userId);
    
    let cleanedCount = 0;
    for (const action of actions) {
      // Clean up delete actions for offline bookmarks that don't exist anymore
      if (action.type === 'delete' && action.bookmarkId?.startsWith('offline_')) {
        const bookmark = await bookmarksStore.get(action.bookmarkId);
        if (!bookmark) {
          await actionsStore.delete(action.id);
          cleanedCount++;
        }
      }
    }
    
    await tx.done;
    return cleanedCount;
  }

  // Clean up orphaned offline bookmarks (bookmarks that have been synced but still marked as offline)
  async cleanupOrphanedOfflineBookmarks(userId) {
    const db = await this.initDB();
    const tx = db.transaction(BOOKMARKS_STORE, 'readwrite');
    const index = tx.store.index('userId');
    const bookmarks = await index.getAll(userId);
    
    // Find offline bookmarks that are older than 1 hour (likely orphaned)
    const oneHourAgo = Date.now() - (60 * 60 * 1000);
    const orphanedOfflineBookmarks = bookmarks.filter(b => 
      b.isOffline && b.createdAt && new Date(b.createdAt).getTime() < oneHourAgo
    );
    
    if (orphanedOfflineBookmarks.length > 0) {
      for (const bookmark of orphanedOfflineBookmarks) {
        await tx.store.delete(bookmark.id);
      }
    }
    
    await tx.done;
  }
}

export const offlineStorageService = new OfflineStorageService();
